import React, { useState } from 'react';
import WaitlistHero from './WaitlistHero';
import './Waitlist.css';

const FEATURES = [
  {
    title: 'Daily AI Chief of Staff',
    body:  'A 60-second check-in becomes 3 specific actions. Your skills, your day, your bigger picture — woven together.',
  },
  {
    title: 'Career intelligence that adapts',
    body:  'Growth tracks where you are. Mirror reflects who you’re becoming. Interview rehearses what comes next.',
  },
  {
    title: 'Quality over quantity',
    body:  'Five focused modules instead of fifty noisy features. Built slowly, on purpose, for people who want depth.',
  },
];

const FAQS = [
  {
    q: 'When does Curx AI launch?',
    a: 'Soon. We’re building this carefully and will email everyone on the waitlist before public launch. Early access is staged — your position determines when you get in.',
  },
  {
    q: 'How does the referral system work?',
    a: 'Every signup gets a unique link. Each friend who joins through your link bumps you 17 spots forward. Refer 3 friends to skip 50 spots.',
  },
  {
    q: 'Will it be free?',
    a: 'There will be a free tier with the core modules. Premium features (advanced AI, team collaboration, voice interview practice) will be a paid tier. Waitlist members get a launch discount.',
  },
  {
    q: 'Who is building this?',
    a: 'Lakshmi Anne, an independent founder. Built with intention and care, not VC speed.',
  },
  {
    q: 'How is my data handled?',
    a: 'Your reflections and check-ins are private to you. We never sell data. AI calls happen on the Anthropic API with no training on your inputs.',
  },
];

export default function WaitlistPage() {
  return (
    <div className="wl-page">
      <ParticleField />
      <nav className="wl-page__nav">
        <a href="/" className="wl-page__back">&larr; Curx AI</a>
        <div className="wl-page__nav-title">
          <span className="wl-page__nav-dot" />
          WAITLIST
        </div>
        <a href="/login" className="wl-page__nav-link">Sign in</a>
      </nav>

      <main className="wl-page__main">
        <WaitlistHero />

        <section className="wl-section">
          <div className="wl-section__head">
            <div className="wl-section__eyebrow">WHY CURX AI</div>
            <h2 className="wl-section__title">Built for <em>thinkers.</em></h2>
          </div>
          <div className="wl-features">
            {FEATURES.map((f, i) => (
              <div key={i} className="wl-feature">
                <div className="wl-feature__num">0{i + 1}</div>
                <h3 className="wl-feature__title">{f.title}</h3>
                <p className="wl-feature__body">{f.body}</p>
              </div>
            ))}
          </div>
        </section>

        <section className="wl-section">
          <div className="wl-section__head">
            <div className="wl-section__eyebrow">AT LAUNCH</div>
            <h2 className="wl-section__title">What you&rsquo;ll get.</h2>
          </div>
          <div className="wl-modules">
            <div className="wl-module"><strong>Growth</strong> · Track skills, reflect, practice interviews, plan growth, forge action plans.</div>
            <div className="wl-module"><strong>System</strong> · Daily AI Chief, health logging, money awareness, paper-planner discipline.</div>
            <div className="wl-module"><strong>Energy</strong> · A weekly conversation that becomes a letter you'll actually read.</div>
          </div>
        </section>

        <section className="wl-section">
          <div className="wl-section__head">
            <div className="wl-section__eyebrow">FAQ</div>
            <h2 className="wl-section__title">Common questions.</h2>
          </div>
          <div className="wl-faqs">
            {FAQS.map((f, i) => <FaqItem key={i} faq={f} />)}
          </div>
        </section>

        <footer className="wl-page__footer">
          <p>&copy; {new Date().getFullYear()} Curx AI &middot; Built with intention.</p>
        </footer>
      </main>
    </div>
  );
}

function FaqItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={'wl-faq' + (open ? ' wl-faq--open' : '')}>
      <button
        type="button"
        className="wl-faq__head"
        onClick={() => setOpen(o => !o)}
        aria-expanded={open}
      >
        <span className="wl-faq__q">{faq.q}</span>
        <span className={'wl-faq__chev' + (open ? ' wl-faq__chev--open' : '')} aria-hidden="true">&rsaquo;</span>
      </button>
      {open && <div className="wl-faq__a">{faq.a}</div>}
    </div>
  );
}

function ParticleField() {
  const canvasRef = React.useRef(null);
  React.useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    let raf;
    const dpr = window.devicePixelRatio || 1;
    const resize = () => {
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.scale(dpr, dpr);
    };
    resize();
    window.addEventListener('resize', resize);
    const particles = Array.from({ length: 40 }).map(() => ({
      x: Math.random() * window.innerWidth,
      y: Math.random() * window.innerHeight,
      vx: (Math.random() - 0.5) * 0.25,
      vy: (Math.random() - 0.5) * 0.25,
      r: Math.random() * 1.4 + 0.4,
      o: Math.random() * 0.4 + 0.2,
    }));
    const animate = () => {
      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);
      particles.forEach(p => {
        p.x += p.vx; p.y += p.vy;
        if (p.x < 0) p.x = window.innerWidth;
        if (p.x > window.innerWidth) p.x = 0;
        if (p.y < 0) p.y = window.innerHeight;
        if (p.y > window.innerHeight) p.y = 0;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = '#c9a227';
        ctx.globalAlpha = p.o * 0.4;
        ctx.fill();
      });
      raf = requestAnimationFrame(animate);
    };
    animate();
    return () => { cancelAnimationFrame(raf); window.removeEventListener('resize', resize); };
  }, []);
  return <canvas ref={canvasRef} className="wl-page__particles" />;
}
